import axios from 'axios';
import { getPortalBySlug } from '../portal/portalConfig';
import {
  buildPortalPath,
  clearPortalAuthData,
  getActivePortalSlug,
  getPortalLocalStorageItem,
} from '../portal/portalStorage';

// API base URL - direct connection to backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
export const FILE_BASE_URL = API_BASE_URL.replace(/\/api$/, '');
// Create axios instance for authenticated requests
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create completely separate axios instance for public requests - no shared defaults
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Explicitly prevent any default authorization headers
  transformRequest: [
    (data, headers) => {
      // Remove any authorization headers before request
      delete headers.Authorization;
      delete headers.authorization;
      delete headers['Authorization'];
      delete headers['authorization'];
      
      if (headers.common) {
        delete headers.common.Authorization;
        delete headers.common.authorization;
      }
      
      return typeof data === 'object' ? JSON.stringify(data) : data;
    }
  ]
});

const getCurrentPortalCode = () => {
  const portalSlug = getActivePortalSlug();
  return getPortalBySlug(portalSlug)?.code || 'TAB1';
};

// Authenticated requests automatically include portal context and portal token.
api.interceptors.request.use(
  (config) => {
    const token = getPortalLocalStorageItem('authToken');
    config.headers = config.headers || {};
    config.headers['X-Portal-Code'] = getCurrentPortalCode();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Public requests include portal context but never include authorization.
publicApi.interceptors.request.use(
  (config) => {
    const cleanHeaders = {
      'Content-Type': 'application/json',
      'X-Portal-Code': getCurrentPortalCode(),
    };

    if (config.headers) {
      Object.keys(config.headers).forEach((key) => {
        const lowerKey = key.toLowerCase();
        if (
          !lowerKey.includes('authorization') &&
          !lowerKey.includes('bearer') &&
          !lowerKey.includes('access-control') &&
          lowerKey !== 'content-type' &&
          lowerKey !== 'x-portal-code'
        ) {
          cleanHeaders[key] = config.headers[key];
        }
      });
    }

    config.headers = cleanHeaders;
    return config;
  },
  (error) => Promise.reject(error)
);

// DO NOT add any request interceptors to publicApi to keep it completely clean
api.reAuthenticate = (password) => {
  return api.post('/security/reauth', { password });
};

api.reAuthenticateManagerDashboard = (password) => {
  return api.post('/security/manager-dashboard/reauth', { password });
};
// Response interceptor for error handling (apply to both instances)
const authErrorHandler = (error) => {
  if (error.response?.status === 401) {
    clearPortalAuthData();
    const loginPath = buildPortalPath('/login');

    if (window.location.pathname !== loginPath) {
      window.location.href = loginPath;
    }
  }

  return Promise.reject(error);
};

const publicErrorHandler = (error) => {
  const message = error.response?.data?.message || 'कुछ गलत हुआ है';
  
  console.error('Public API Error:', {
    status: error.response?.status,
    url: error.config?.url,
    headers: error.config?.headers,
    message: message
  });
  
  // Removed all toast error messages to prevent unwanted popups
  
  return Promise.reject(error);
};

api.interceptors.response.use(
  (response) => response,
  authErrorHandler
);

publicApi.interceptors.response.use(
  (response) => response,
  publicErrorHandler
);

export default api;
export { api, publicApi };

// User profile field lock APIs
api.getProfileFieldLocks = () => {
  return api.get('/users/profile-field-locks');
};

api.getProfileById = (userId) => {
  return api.get(`/users/${userId}`);
};

api.updateProfileById = (userId, payload) => {
  return api.put(`/users/${userId}`, payload);
};
// Admin API functions
const adminAPI = {
  // Get all users with pagination and filters
  getUsers: (page = 0, size = 20, filters = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      ...filters
    });
    return api.get(`/admin/users?${params}`);
  },
  getProfileFieldLocks: () => api.get('/admin/settings/profile-field-locks'),

updateProfileFieldLocks: (payload) =>
  api.put('/admin/settings/profile-field-locks', payload),

  // Get specific user by ID
  getUser: (id) => {
    return api.get(`/admin/users/${id}`);
  },
 rebalancePools: (overwrite = true) =>
  api.post(`/pools/rebalance?overwrite=${overwrite}`),
  // Block user
  blockUser: (id) => {
    return api.put(`/admin/users/${id}/block`);
  },

  // Unblock user
  unblockUser: (id) => {
    return api.put(`/admin/users/${id}/unblock`);
  },
// Restore user (NEW API)
restoreUser: (id) => {
  return api.put(`/admin/users/${id}/restore`);
},
exportUsersByJoiningDate: (params = {}) => {
  return api.get('/admin/export/users/joining-date', {
    params,
    responseType: 'blob',
  });
},

exportUsersByRetirementDate: (params = {}) => {
  return api.get('/admin/export/users/retirement-date', {
    params,
    responseType: 'blob',
  });
},
  // Soft delete user
  deleteUser: (id) => {
    return api.delete(`/admin/users/${id}`);
  },
// Update user (Admin)
updateUser: (id, payload) => {
  return api.put(`/admin/users/${id}`, payload);
},
updateMemberStatus: (id, payload) => {
  return api.put(`/admin/users/${id}/member-status`, payload);
},

exportSahyog: (params = {}) => {
  return api.get('/admin/export/sahyog', {
    params,
    responseType: 'blob'
  });
},
getHomeDisplayContentSettings: () => {
  return api.get('/admin/settings/home-display-content');
},

updateHomeDisplayContentSettings: (payload) => {
  return api.put('/admin/settings/home-display-content', payload);
},
exportAsahyog: (params = {}) => {
  return api.get('/admin/export/asahyog', {
    params,
    responseType: 'blob'
  });
},
manualMoveAsahyogToSahyog: (payload) => {
  return api.post('/admin/manual-sahyog/move', payload);
},
exportSahyogByBeneficiary: (params = {}) => {
  return api.get('/admin/export/sahyog/by-beneficiary', {
    params,
    responseType: 'blob'
  });
},
// Blog Management
getBlogs: () => {
  return api.get('/admin/blogs');
},

createBlog: (formData) => {
  return api.post('/admin/blogs', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
},

updateBlog: (blogId, formData) => {
  return api.put(`/admin/blogs/${blogId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
},

deleteBlog: (blogId) => {
  return api.delete(`/admin/blogs/${blogId}`);
},
exportAsahyogByBeneficiary: (params = {}) => {
  return api.get('/admin/export/asahyog/by-beneficiary', {
    params,
    responseType: 'blob'
  });
},
exportZeroUtrMembers: (params = {}) => {
  return api.get('/admin/monthly-sahyog/no-utr-ever/export', {
    params,
    responseType: 'blob'
  });
},
exportNoLoginThreeMonths: (params = {}) => {
  return api.get('/admin/export/retention/no-login-3-months', {
    params,
    responseType: 'blob',
  });
},

exportNoSahyogTwoMonths: (params = {}) => {
  return api.get('/admin/export/retention/no-sahyog-2-months', {
    params,
    responseType: 'blob',
  });
},
updateSahyogReceipt: (receiptId, payload) => {
  return api.put(`/admin/monthly-sahyog/receipts/${receiptId}`, payload);
},

deleteSahyogReceipt: (receiptId) => {
  return api.delete(`/admin/monthly-sahyog/receipts/${receiptId}`);
},
exportAllSahyog: (params = {}) => {
  return api.get('/admin/export/sahyog/all', {
    params,
    responseType: 'blob'
  });
},
  softDeleteUserWithApproval: (userId, payload = {}) =>
    api.post(`/delete-approval/users/${userId}/soft-delete`, payload),

  restoreDeletedUser: (userId) =>
    api.post(`/delete-approval/users/${userId}/restore`),

  getPendingDeleteRequests: () =>
    api.get(`/delete-approval/requests/pending`),

  getMyDeleteRequests: () =>
    api.get(`/delete-approval/requests/my`),

  approveDeleteRequest: (requestId) =>
    api.post(`/delete-approval/requests/${requestId}/approve`),

  rejectDeleteRequest: (requestId, payload = {}) =>
    api.post(`/delete-approval/requests/${requestId}/reject`, payload),

  getDeletedUsersFromApprovalFlow: () =>
    api.get(`/delete-approval/trash/users`),
exportAllAsahyog: () => {
  return api.get('/admin/export/asahyog/all', {
    responseType: 'blob'
  });
},
exportPendingProfiles: (params = {}) => {
  return api.get('/users/pending-profiles/export', {
    params,
    responseType: 'blob'
  });
},
getPendingProfilesLiveLink: (params = {}) => {
  return api.get('/admin/export/pending-profiles/live-link', {
    params,
  });
},
getExportMobileNumberSetting: () => {
  return api.get('/admin/settings/export-mobile-number');
},
getSelfDonationVisibleSetting: () => {
  return api.get('/admin/settings/self-donation-visible');
},
getSahyogPaymentSettings: () => {
  return api.get('/admin/settings/sahyog-payment');
},
updateSahyogPaymentSettings: (payload) => {
  return api.put('/admin/settings/sahyog-payment', payload);
},
getDistrictManagerExportMobileSetting: () => {
  return api.get('/admin/settings/district-manager-export-mobile');
},

updateDistrictManagerExportMobileSetting: (enabled) => {
  return api.put('/admin/settings/district-manager-export-mobile', { enabled });
},

getBlockManagerExportMobileSetting: () => {
  return api.get('/admin/settings/block-manager-export-mobile');
},

updateBlockManagerExportMobileSetting: (enabled) => {
  return api.put('/admin/settings/block-manager-export-mobile', { enabled });
},

updateSelfDonationVisibleSetting: (enabled) => {
  return api.put('/admin/settings/self-donation-visible', { enabled });
},
sendInsuranceInquiriesExportEmail: () =>
  api.post('/admin/export/insurance-inquiries/email'),
getSelfDonationQr: () => {
  return api.get('/admin/settings/self-donation-qr');
},

// Admin Report Table APIs
getUsersByJoiningDateReport: (params = {}) => {
  return api.get('/admin/reports/users-by-joining-date', { params });
},

getUsersByRetirementDateReport: (params = {}) => {
  return api.get('/admin/reports/users-by-retirement-date', { params });
},

getNoLoginThreeMonthsReport: (params = {}) => {
  return api.get('/admin/reports/no-login-three-months', { params });
},

getNoSahyogTwoMonthsReport: (params = {}) => {
  return api.get('/admin/reports/no-sahyog-two-months', { params });
},
uploadSelfDonationQr: (file) => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post('/admin/settings/self-donation-qr', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
},
  // Get single death case by id
  getDeathCaseById: (id) => {
    return api.get(`/death-cases/${id}`);
  },

  // Update death case
  updateDeathCase: (id, formData) => {
    return api.put(`/death-cases/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  // Export Mobile Permission APIs - New user-specific flow
getExportMobilePermissions: (params = {}) => {
  return api.get('/admin/export-mobile-permissions', { params });
},

checkExportMobilePermission: (userId) => {
  return api.get(`/admin/export-mobile-permissions/check/${userId}`);
},

grantExportMobilePermission: (payload) => {
  return api.post('/admin/export-mobile-permissions/grant', payload);
},

revokeExportMobilePermission: (payload) => {
  return api.post('/admin/export-mobile-permissions/revoke', payload);
},
getPublicSelfDonationSettings: () => {
  return publicApi.get('/public/self-donation-settings');
},

updateExportMobileNumberSetting: (enabled) => {
  return api.put('/admin/settings/export-mobile-number', { enabled });
},
  getMobileOtpSetting: () => {
    return api.get('/admin/settings/mobile-otp');
  },
// Manager Delete Permission APIs - User-specific manager delete access
getManagerDeletePermissions: (params = {}) => {
  return api.get('/admin/manager-delete-permissions', { params });
},

checkManagerDeletePermission: (userId) => {
  return api.get(`/admin/manager-delete-permissions/check/${userId}`);
},

grantManagerDeletePermission: (payload) => {
  return api.post('/admin/manager-delete-permissions/grant', payload);
},

revokeManagerDeletePermission: (payload) => {
  return api.post('/admin/manager-delete-permissions/revoke', payload);
},
  updateMobileOtpSetting: (enabled) => {
    return api.put('/admin/settings/mobile-otp', { enabled });
  },
  bulkPasswordReset: (file, defaultPassword = 'Shub@123') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('defaultPassword', defaultPassword);

  return api.post('/admin/users/bulk-password-reset', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
},
bulkLocationUpdate: (file, dryRun = true) => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post(`/admin/users/bulk-location-update?dryRun=${dryRun}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
},
  resetManagerDashboardPassword: (id, payload) =>
  api.put(`/admin/users/${id}/manager-dashboard-password-reset`, payload),
// Admin password reset (no current password required)
resetUserPassword: (id, payload) =>
  api.put(`/admin/users/${id}/password-reset`, payload),
// Permanent delete user (NEW API)
permanentDeleteUser: (id) => {
  return api.post(`/delete-approval/users/${id}/permanent-delete`);
},

  // Update user role
  updateUserRole: (id, role) => {
    return api.put(`/admin/users/${id}/role`, { role });
  },

  // Export users as CSV
exportUsers: (params = {}) => {
  return api.get('/users/export', {
    params,
    responseType: 'blob'
  });
},

exportAllUsers: () => {
  return api.get('/admin/users/export-all', {
    responseType: 'blob'
  });
},
logoutAllUsers: () => {
  return api.post('/admin/security/logout-all-users');
},

checkSessionStatus: () => {
  return api.get('/admin/security/session-status');
},

  // Get all death cases
  getDeathCases: () => {
    return api.get('/death-cases');
  },

  // Create death case
  createDeathCase: (formData) => {
    return api.post('/death-cases', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // Open death case
  openDeathCase: (id) => {
    return api.put(`/death-cases/${id}/show`);
  },

  // Close death case
  closeDeathCase: (id) => {
    return api.put(`/death-cases/${id}/hide`);
  },

  // Activate death case (kept for backward compatibility)
  activateDeathCase: (id) => {
    return api.put(`/death-cases/${id}/activate`);
  },

  // Deactivate death case (kept for backward compatibility)
  deactivateDeathCase: (id) => {
    return api.put(`/death-cases/${id}/deactivate`);
  },

  // Hide death case (Admin only)
  hideDeathCase: (id) => {
    return api.put(`/death-cases/${id}/hide`);
  },

  // Show/Unhide death case (Admin only)
  showDeathCase: (id) => {
    return api.put(`/death-cases/${id}/show`);
  },

  // Get dashboard stats (if you have this endpoint)
  getDashboardStats: () => {
    return api.get('/admin/dashboard/stats');
  },

  // Export death cases to Excel
  exportDeathCases: () => {
    return api.get('/death-cases/export', {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
  },

  // Get all receipts
  // getReceipts: (params = {}) => {
  //   return api.get('/receipts', { params });
  // },

  //new endpoint to get all receipts with pagination and filters
  // Get all receipts with pagination
// Get receipts with pagination and optional UTR search
getReceipts: (params = {}) => {
  return api.get('/admin/receipts/all', {
    params,
  });
},

// Export receipts with optional UTR search
exportReceipts: (params = {}) => {
  return api.get('/admin/receipts/export', {
    params,
    responseType: 'blob',
    headers: {
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  });
},

  // Get manager assignments
  getManagerAssignments: (params = {}) => {
    return api.get('/manager/assignments', { params });
  },

  // Create manager assignment
  createManagerAssignment: (assignmentData) => {
    return api.post('/manager/assignments', assignmentData);
  },

  // Remove manager assignment
  removeManagerAssignment: (assignmentId) => {
    return api.delete(`/manager/assignments/${assignmentId}`);
  },

  // Remove ALL manager assignments for a user
  removeAllManagerAccess: (userId) => {
    return api.delete(`/manager/assignments/user/${userId}`);
  },
  getHomeStatsSettings: () => {
  return api.get('/admin/settings/home-stats');
},

updateHomeStatsSettings: (payload) => {
  return api.put('/admin/settings/home-stats', payload);
},

  // Get manager scope for a specific user
  getManagerScope: (managerId) => {
    return api.get('/manager/scope', { params: { managerId } });
  },
  permanentlyDeleteUserFromTrash: (userId) =>
  api.post(`/delete-approval/users/${userId}/permanent-delete`),

  // Location APIs - Using same pattern as registration
  getLocationHierarchy: () => {
    return api.get('/locations/hierarchy');
  },
  getAuditLogsByUser: (userId) =>
  api.get(`/audit-logs/user/${userId}`),

getAuditLogsByEntity: (entityType, entityId) =>
  api.get(`/audit-logs/entity`, {
    params: { entityType, entityId }
  }),
  restoreAllDeletedUsers: () =>
  api.post(`/delete-approval/trash/users/restore-all`),

clearAllTrashUsers: () =>
  api.post(`/delete-approval/trash/users/clear-all`),
getAuditLogs: (page = 0, size = 20) =>
  api.get(`/audit-logs`, { params: { page, size } }),
checkManualCreateUserMatch: (payload) => {
  return api.post('/admin/users/manual-create/check', payload);
},

manualCreateUser: (payload) => {
  return api.post('/admin/users/manual-create', payload);
},
};

// Manager API endpoints
const managerAPI = {
  // Manager Assignment APIs
  createAssignment: (assignmentData) => api.post('/manager/assignments', assignmentData),
  getAssignments: (params = {}) => api.get('/manager/assignments', { params }),
  getAssignmentsByLocation: (params = {}) => api.get('/manager/assignments/location', { params }),
  removeAssignment: (assignmentId) => api.delete(`/manager/assignments/${assignmentId}`),

  // Manager User Management APIs
  getAccessibleUsers: (params = {}) => api.get('/manager/users', { params }),
  getUsersByLocation: (params = {}) => api.get('/manager/users/location', { params }),
  getUserStats: () => api.get('/manager/users/stats'),
  blockUser: (userId, reason) => api.put(`/manager/users/${userId}/block`, { reason }),
  unblockUser: (userId) => api.put(`/manager/users/${userId}/unblock`),
  updateUserRole: (userId, role) => api.put(`/manager/users/${userId}/role`, { role }),
  softDeleteUserWithApproval: (userId, payload = {}) =>
    api.post(`/delete-approval/users/${userId}/soft-delete`, payload),

  getMyDeleteRequests: () =>
    api.get(`/delete-approval/requests/my`),
  // Manager Query System APIs
  createQuery: (queryData) => api.post('/manager/queries', queryData),
  getQueries: (params = {}) => api.get('/manager/queries', { params }),
  getQueryStats: () => api.get('/manager/queries/stats'),
  assignQuery: (queryId, managerId) => api.put(`/manager/queries/${queryId}/assign`, { managerId }),
  updateQueryStatus: (queryId, status) => api.put(`/manager/queries/${queryId}/status`, { status }),
  resolveQuery: (queryId, resolution) => api.put(`/manager/queries/${queryId}/resolve`, { resolution }),
  escalateQuery: (queryId) => api.put(`/manager/queries/${queryId}/escalate`),
  getPendingQueries: (params = {}) => api.get('/manager/queries/pending', { params }),
  getUrgentQueries: (params = {}) => api.get('/manager/queries/urgent', { params }),
  getMyQueries: (params = {}) => api.get('/manager/queries/my-queries', { params }),
exportUsers: (params = {}) => {
  return api.get('/users/export', {
    params,
    responseType: 'blob'
  });
},
exportUsersByJoiningDate: (params = {}) => {
  return api.get('/admin/export/users/joining-date', {
    params,
    responseType: 'blob',
  });
},

exportUsersByRetirementDate: (params = {}) => {
  return api.get('/admin/export/users/retirement-date', {
    params,
    responseType: 'blob',
  });
},
exportNoLoginThreeMonths: (params = {}) => {
  return api.get('/admin/export/retention/no-login-3-months', {
    params,
    responseType: 'blob',
  });
},

exportNoSahyogTwoMonths: (params = {}) => {
  return api.get('/admin/export/retention/no-sahyog-2-months', {
    params,
    responseType: 'blob',
  });
},
resetUserPassword: (userId, payload) =>
  api.put(`/manager/users/${userId}/password-reset`, payload),
// Ticket / Query System
getQueries: (params = {}) =>
  api.get('/manager/queries', { params }),

getQueryById: (queryId) =>
  api.get(`/manager/queries/${queryId}`),

getQueryMessages: (queryId) =>
  api.get(`/manager/queries/${queryId}/messages`),

addQueryMessage: (queryId, message) =>
  api.post(`/manager/queries/${queryId}/messages`, { message }),

createQuery: (queryData) =>
  api.post('/manager/queries', queryData),

updateQueryStatus: (queryId, status) =>
  api.put(`/manager/queries/${queryId}/status`, { status }),

resolveQuery: (queryId, resolution) =>
  api.put(`/manager/queries/${queryId}/resolve`, { resolution }),

escalateQuery: (queryId) =>
  api.put(`/manager/queries/${queryId}/escalate`),
exportPendingProfiles: (params = {}) => {
  return api.get('/admin/export/pending-profiles', {
    params,
    responseType: 'blob'
  });
},
getPendingProfilesLiveLink: (params = {}) => {
  return api.get('/admin/export/pending-profiles/live-link', {
    params,
  });
},
exportZeroUtrMembers: (params = {}) => {
  return api.get('/admin/monthly-sahyog/no-utr-ever/export', {
    params,
    responseType: 'blob'
  });
},

exportSahyog: (params = {}) => {
  return api.get('/admin/export/sahyog', {
    params,
    responseType: 'blob'
  });
},

exportAsahyog: (params = {}) => {
  return api.get('/admin/export/asahyog', {
    params,
    responseType: 'blob'
  });
},

exportSahyogByBeneficiary: (params = {}) => {
  return api.get('/admin/export/sahyog/by-beneficiary', {
    params,
    responseType: 'blob'
  });
},

exportAsahyogByBeneficiary: (params = {}) => {
  return api.get('/admin/export/asahyog/by-beneficiary', {
    params,
    responseType: 'blob'
  });
},

exportAllSahyog: (params = {}) => {
  return api.get('/admin/export/sahyog/all', {
    params,
    responseType: 'blob'
  });
},

exportAllAsahyog: (params = {}) => {
  return api.get('/admin/export/asahyog/all', {
    params,
    responseType: 'blob'
  });
},
  // Manager Dashboard APIs
  getDashboardOverview: () => api.get('/manager/dashboard/overview'),
  getManagerScope: (params = {}) => api.get('/manager/scope', { params }),
  getQuickStats: () => api.get('/manager/dashboard/stats'),
  getAlerts: () => api.get('/manager/dashboard/alerts'),
  getPermissions: () => api.get('/manager/dashboard/permissions')
};

const publicAPI = {
  submitInsuranceInquiry: (payload) => {
    return publicApi.post('/public/insurance-inquiries', payload);
  },

  getBlogs: () => {
    return publicApi.get('/public/blogs');
  },

  getBlogById: (blogId) => {
    return publicApi.get(`/public/blogs/${blogId}`);
  },
};

publicApi.getHomeDisplayContent = () => {
  return publicApi.get('/public/home-display-content');
};
publicApi.getHomeStats = () => {
  return publicApi.get('/public/home-stats');
};
publicApi.getSahyogPaymentSettings = () => {
  return publicApi.get('/public/sahyog-payment-settings');
};
const receiptAPI = {
  uploadReceipt: (payload) => {
    return api.post('/receipts', payload);
  },

  uploadPublicReceipt: (payload) => {
    return publicApi.post('/receipts/public-upload', payload);
  },
};
// Export all APIs
export { adminAPI, managerAPI, publicAPI, receiptAPI };